import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, saveAuth } from "@/lib/api";
import BrandHeader from "@/components/BrandHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LogIn, MailCheck, KeyRound } from "lucide-react";

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(null); // { email } | null
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [autoForgotSuggested, setAutoForgotSuggested] = useState(false);
  const FAIL_THRESHOLD = 5;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setNeedsVerification(null);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      saveAuth(data.token, data.user);
      setFailedAttempts(0);
      toast.success(`Bienvenido, ${data.user.name}`);
      const dest =
        data.user.role === "admin"
          ? "/admin"
          : data.user.role === "customer"
          ? "/mi-cuenta"
          : "/worker";
      navigate(dest);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      // Structured error: unverified email
      if (detail && typeof detail === "object" && detail.code === "email_not_verified") {
        setNeedsVerification({ email: detail.email || email });
        toast.error("Tu correo aún no está confirmado.");
      } else {
        const msg = typeof detail === "string" ? detail : "Credenciales inválidas";
        toast.error(msg);
        // Track invalid credentials (401) — after 5 failures, suggest reset.
        if (status === 401) {
          setFailedAttempts((prev) => {
            const next = prev + 1;
            if (next >= FAIL_THRESHOLD && !autoForgotSuggested) {
              setAutoForgotSuggested(true);
              setForgotOpen(true);
              setForgotEmail(email);
              setForgotSent(false);
              setTimeout(() => {
                toast.info("Parece que olvidaste tu contraseña. Te ayudamos a recuperarla.");
                const el = document.querySelector('[data-testid="forgot-password-panel"]');
                if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
              }, 200);
            }
            return next;
          });
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const resendConfirmation = async () => {
    setLoading(true);
    try {
      await api.post("/customers/resend-confirmation", { email: needsVerification.email });
      toast.success("Nuevo enlace enviado. Revisa tu correo.");
    } catch {
      toast.error("Error al reenviar el correo");
    } finally {
      setLoading(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    const target = (forgotEmail || email || "").trim();
    if (!target) {
      toast.error("Ingresa tu email para recuperar la contraseña");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: target });
      setForgotSent(true);
      toast.success("Si el correo está registrado, te enviamos un enlace de recuperación.");
    } catch {
      // Backend responds 200 always — real error means network/500
      toast.error("No se pudo enviar la solicitud. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <BrandHeader />
      <main className="max-w-md mx-auto px-6 py-16 md:py-24">
        <div className="glass rounded-xl p-8 md:p-10">
          <div className="text-xs uppercase tracking-[0.3em] text-yellow-400 font-mono-display mb-3">
            Acceso restringido
          </div>
          <h1 className="font-display font-black text-3xl mb-2">Inicia sesión</h1>
          <p className="text-zinc-400 mb-8 text-sm">Solo administrador y trabajadores autorizados.</p>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs uppercase tracking-widest text-zinc-400 font-mono-display">
                Email
              </Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-zinc-900 border-white/10 text-white focus-visible:ring-yellow-400 focus-visible:border-yellow-400"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pw" className="text-xs uppercase tracking-widest text-zinc-400 font-mono-display">
                Contraseña
              </Label>
              <Input
                id="pw"
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-zinc-900 border-white/10 text-white focus-visible:ring-yellow-400 focus-visible:border-yellow-400"
                required
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full bg-yellow-400 hover:bg-yellow-300 text-black font-bold h-11"
            >
              <LogIn className="w-4 h-4 mr-2" />
              {loading ? "Ingresando..." : "Ingresar"}
            </Button>

            <div className="text-center pt-1">
              <button
                type="button"
                onClick={() => {
                  setForgotOpen((v) => !v);
                  setForgotEmail(email);
                  setForgotSent(false);
                }}
                className="text-xs text-zinc-400 hover:text-yellow-400 underline underline-offset-4"
                data-testid="forgot-password-toggle"
              >
                ¿Olvidaste tu contraseña?
              </button>
            </div>
          </form>

          {forgotOpen && (
            <div
              data-testid="forgot-password-panel"
              className={`mt-6 border rounded-lg p-4 ${
                autoForgotSuggested
                  ? "border-yellow-400 bg-yellow-400/10 ring-2 ring-yellow-400/40"
                  : "border-yellow-400/30 bg-yellow-400/5"
              }`}
            >
              {autoForgotSuggested && !forgotSent && (
                <div
                  className="mb-3 text-xs text-yellow-200 bg-yellow-400/10 border border-yellow-400/40 rounded-md px-3 py-2"
                  data-testid="auto-forgot-hint"
                >
                  Detectamos {failedAttempts} intentos fallidos. ¿Olvidaste tu contraseña? Te ayudamos a recuperarla.
                </div>
              )}
              <div className="flex items-start gap-3">
                <KeyRound className="w-5 h-5 text-yellow-400 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="font-bold text-yellow-300 text-sm mb-1">Recuperar contraseña</div>
                  {forgotSent ? (
                    <div
                      className="text-xs text-zinc-300 leading-relaxed"
                      data-testid="forgot-password-sent-msg"
                    >
                      Si <b className="text-yellow-200 break-all">{forgotEmail}</b> está registrado, te
                      enviamos un enlace para restablecer tu contraseña. Revisa tu correo (y la carpeta
                      de spam). El enlace expira en 30 minutos.
                    </div>
                  ) : (
                    <form onSubmit={submitForgot} className="space-y-2">
                      <div className="text-xs text-zinc-400 leading-relaxed mb-2">
                        Ingresa el correo de tu cuenta y te enviaremos un enlace para elegir una nueva
                        contraseña.
                      </div>
                      <Input
                        type="email"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        placeholder="tucorreo@ejemplo.com"
                        className="bg-zinc-900 border-white/10 text-white focus-visible:ring-yellow-400 focus-visible:border-yellow-400 h-9 text-sm"
                        data-testid="forgot-password-email-input"
                        required
                      />
                      <Button
                        type="submit"
                        disabled={loading}
                        data-testid="forgot-password-submit-btn"
                        size="sm"
                        className="w-full bg-yellow-400 hover:bg-yellow-300 text-black font-bold h-9"
                      >
                        Enviar enlace de recuperación
                      </Button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          )}

          {needsVerification && (
            <div
              data-testid="login-needs-verification"
              className="mt-6 border border-yellow-400/40 bg-yellow-400/5 rounded-lg p-4"
            >
              <div className="flex items-start gap-3">
                <MailCheck className="w-5 h-5 text-yellow-400 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="font-bold text-yellow-300 text-sm mb-1">
                    Confirma tu correo antes de entrar
                  </div>
                  <div className="text-xs text-zinc-400 leading-relaxed mb-3">
                    Enviamos el enlace a <b className="text-yellow-200 break-all">{needsVerification.email}</b>.
                    Si no lo encuentras, revisa la carpeta de spam o solicita un reenvío.
                  </div>
                  <Button
                    onClick={resendConfirmation}
                    disabled={loading}
                    data-testid="login-resend-verification-btn"
                    size="sm"
                    className="w-full bg-yellow-400 hover:bg-yellow-300 text-black font-bold h-9"
                  >
                    Reenviar correo de confirmación
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
